/**
 * Shared `mailto:` builder for the operational email templates (#254).
 *
 * PTSB agents send a number of operational requests (hold removals, SEPA
 * recalls, data-breach notifications, …) by email so they can be actioned from
 * Outlook. The source-of-truth wording lives in the Drive templates the
 * operator maintains; we mirror it in {@link EmailTemplate} definitions and
 * "pop" it as a `mailto:` link so the agent's default mail client (Outlook)
 * opens a pre-composed draft.
 *
 * This module is deliberately **client-side only**: a `mailto:` URL is a
 * browser/OS concern (no PII leaves the app, nothing is sent to the BFF), which
 * keeps it simple and lets the agent review + send in Outlook exactly as the
 * operator asked.
 *
 * Prefill is best-effort: the caller passes whatever in-context detail it
 * already has (customer name, CIF, selected account, agent identifier) and the
 * template's `build` maps those onto its own field lines. Anything unknown is
 * left blank for the agent to fill. **Never** put a full PAN or other secret in
 * a body — use masked / last-4 values (see {@link maskCard}).
 */

/**
 * Context an agent screen can supply to prefill a template. Every field is
 * optional — templates fill what they can and leave the rest blank.
 */
export interface TemplateContext {
  /** Identified customer's full name. */
  customerName?: string;
  /** Customer CIF number. */
  cif?: string;
  /** Selected/in-context account number. */
  accountNumber?: string;
  /** A card number — will be masked to last-4 before use; never render in full. */
  cardNumber?: string;
  /** The agent's identifier (UPN) for "Agent Name" style fields. */
  agentName?: string;
  /** SEPA / other destination (beneficiary) name for the transaction. */
  destinationName?: string;
  /** Posting date of the transaction (as displayed on-screen). */
  postingDate?: string;
  /** Transaction reference (as displayed on-screen). */
  transactionReference?: string;
  /** Transaction amount (as displayed on-screen, e.g. formatted currency). */
  transactionAmount?: string;
  /** A new mobile number the agent has captured (as displayed on-screen). */
  newMobileNumber?: string;
  /** A payment sequence number (e.g. the Direct Debit's Unibanks PaymentsSeq). */
  sequenceNumber?: string;
  /** The customer's Open24 (online-banking) number, as displayed on-screen. */
  open24Number?: string;
  /**
   * Resolved content-catalogue overrides (#637); the `key → string` map from
   * `useContentMap()`, threaded in by the screen. When absent, templates use
   * their compiled-in default wording (verbatim), so a template built without a
   * `content` map renders byte-identical to before this field existed. Each
   * editable template reads a fixed sentence via `ctx.content?.[key] ?? default`
   * and substitutes live-detail tokens; it resolves ONLY to plain strings — the
   * layout/HTML structure stays fixed in code (text only, never logic).
   */
  content?: Readonly<Record<string, string>>;
}

/** A single operational email template (mirrors a Drive `.msg`). */
export interface EmailTemplate {
  /** Stable id (used as the React key / test hook). */
  readonly id: string;
  /** Human label for the action button ("R Hold Removal"). */
  readonly label: string;
  /** Fixed recipient(s), when the template targets a known mailbox. */
  readonly to?: string;
  /** Fixed CC recipient(s), when the template copies a known mailbox. */
  readonly cc?: string;
  /**
   * Builds the concrete subject + body from the supplied context. Pure and
   * synchronous so it is trivially unit-testable.
   */
  build(ctx: TemplateContext): { subject: string; body: string };
  /**
   * Optionally builds a formatted **HTML** body from the supplied context.
   *
   * When present, the template is delivered as a downloadable Outlook draft
   * (`.eml`, see {@link ./eml.js}) that carries the HTML look-and-feel
   * (headings, bold labels, branding) instead of a plain-text `mailto:` link —
   * `mailto:` bodies are plain-text only. When absent, the template falls back
   * to the `mailto:` path built from {@link build}. Pure + synchronous. Must
   * HTML-escape any interpolated context values, and never render a full PAN.
   */
  buildHtml?(ctx: TemplateContext): string;
}

/**
 * Masks a card number to its last 4 digits (`•••• 1234`), per the card-display
 * rules — a full PAN must never appear in an email body. Returns an empty
 * string for an absent/short value.
 */
export function maskCard(cardNumber?: string): string {
  if (!cardNumber) return '';
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '';
  return `•••• ${digits.slice(-4)}`;
}

/**
 * Renders a card number in FULL (all digits, no masking) for the email-template
 * bodies — the receiving back-office teams (Unibanks/TSYS updates, gambling
 * block, Apple/Google Pay token activation) need the complete card number to
 * action the request. Full-PAN display is normally dashboard-only (#97), but
 * @dylan219 explicitly authorised the full 16-digit card number on the email
 * templates specifically (#434, 2026-07-21): "it needs to be the full 16 digit
 * card number and should not have the first 12 restricted as you are currently
 * doing." Returns the digits only (non-digit separators stripped), or an empty
 * string for an absent value. Distinct from {@link maskCard} — use that
 * everywhere else.
 */
export function fullCard(cardNumber?: string): string {
  if (!cardNumber) return '';
  return cardNumber.replace(/\D/g, '');
}

/**
 * Builds a `mailto:` URL with the subject and body correctly percent-encoded.
 *
 * `mailto:` requires the query component (`subject`, `body`) to be
 * percent-encoded; notably spaces become `%20` (not `+`) and newlines become
 * `%0D%0A`. `encodeURIComponent` handles this — we normalise line endings to
 * CRLF first so Outlook renders the body with the intended line breaks.
 */
export function buildMailtoUrl(input: {
  to?: string;
  subject: string;
  body: string;
}): string {
  const to = input.to ?? '';
  const normalisedBody = input.body.replace(/\r\n|\r|\n/g, '\r\n');
  const params: string[] = [];
  params.push(`subject=${encodeURIComponent(input.subject)}`);
  params.push(`body=${encodeURIComponent(normalisedBody)}`);
  return `mailto:${encodeURIComponent(to)}?${params.join('&')}`;
}

/**
 * Builds the `mailto:` URL for a template + context and opens it, so the
 * agent's default mail client composes the email. Returns the URL that was
 * opened (useful for tests / logging).
 *
 * The `opener` seam defaults to `window.location.assign` but is injectable so
 * tests don't navigate the JSDOM window.
 */
export function openTemplateEmail(
  template: EmailTemplate,
  ctx: TemplateContext,
  opener: (url: string) => void = (url) => {
    window.location.assign(url);
  },
): string {
  const { subject, body } = template.build(ctx);
  const url = buildMailtoUrl({ to: template.to, subject, body });
  opener(url);
  return url;
}
